import React from 'react';
import styled from 'styled-components';
import type { AdminMemberSearchData } from '../../../shared/dto/EntityRepository';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import {
  BlackFitBtn,
  PaperBlurFitBtn,
} from '../../../shared/component/headless/button/BtnBody';
import { getCareerPeriodType } from './careerBulkConfig';
import type { CareerBulkSheetParseResult } from './parseCareerBulkSheet';

interface CareerBulkPreviewProps {
  fileName: string;
  targetMember: AdminMemberSearchData;
  result: CareerBulkSheetParseResult;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const CareerBulkPreview: React.FC<CareerBulkPreviewProps> = ({
  fileName,
  targetMember,
  result,
  isSubmitting,
  onCancel,
  onConfirm,
}) => {
  const rows = [
    ...result.careerList.map(({ rowNumber, career }) => ({
      rowNumber,
      title: career.content,
      ready: true,
      message: `${career.careerType} · ${getCareerPeriodType(career.careerType)}`,
    })),
    ...result.invalidResults.map((item) => ({
      rowNumber: item.rowNumber,
      title: '',
      ready: false,
      message: item.message ?? 'Invalid row',
    })),
  ].sort((left, right) => left.rowNumber - right.rowNumber);

  return (
    <Container aria-label="Career bulk import preview">
      <Title>Import preview</Title>
      <Meta>{targetMember.username} · @{targetMember.handle || '—'}</Meta>
      <FileName title={fileName}>{fileName}</FileName>
      <Summary>
        {result.totalCount} rows · {result.careerList.length} ready · {result.invalidResults.length} error
      </Summary>
      {result.invalidResults.length > 0 && (
        <Notice>Error rows will be skipped.</Notice>
      )}
      <Notice>Submitting the same rows again creates duplicates.</Notice>
      <List>
        {rows.map((item) => (
          <Item key={item.rowNumber} $ready={item.ready}>
            <RowMeta>
              Row {item.rowNumber}{item.title ? ` · ${item.title}` : ''}
            </RowMeta>
            <Status $ready={item.ready}>{item.ready ? 'Ready' : 'Error'}</Status>
            <Message>{item.message}</Message>
          </Item>
        ))}
      </List>
      <Buttons>
        <HeadlessBtn
          value={isSubmitting ? 'Creating...' : 'Create'}
          handleClick={onConfirm}
          disabled={isSubmitting || result.careerList.length === 0}
          StyledBtn={BlackFitBtn}
        />
        <HeadlessBtn
          value="Cancel"
          handleClick={onCancel}
          disabled={isSubmitting}
          StyledBtn={PaperBlurFitBtn}
        />
      </Buttons>
    </Container>
  );
};

const Container = styled.section`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Title = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Meta = styled.div`
  margin-top: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const FileName = styled.div`
  overflow: hidden;
  margin-top: 3px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
`;

const Summary = styled.div`
  padding: 14px 0 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Notice = styled.div`
  padding-bottom: 5px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const List = styled.div`
  min-height: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar { display: none; }
`;

const Item = styled.article<{ $ready: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  opacity: ${({ $ready }) => ($ready ? 1 : 0.8)};
`;

const RowMeta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  word-break: break-word;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const Status = styled.div<{ $ready: boolean }>`
  color: ${({ theme, $ready }) =>
    $ready ? theme.colors.color_Gray_05 : theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_04};
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  word-break: break-word;
  ${({ theme }) => theme.typography.Body_04};
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
`;

export default CareerBulkPreview;
